import React, { useEffect, useRef } from "react";
import * as THREE from "three";

// Interactive product cube: 6-face box.
//  - top (+Y): fixed engraved plate (QR, "MENU", NFC, cube logo) — not customizable
//  - bottom (-Y): matte black PVC base — not customizable
//  - 4 sides: customizable color/finish/material + optional logo or text
// Drag to rotate (mouse + touch); gentle auto-rotate when idle.

const readable = (hex) => {
  const c = hex.replace("#", "");
  const r = parseInt(c.substr(0, 2), 16), g = parseInt(c.substr(2, 2), 16), b = parseInt(c.substr(4, 2), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 140 ? "#0B0B0C" : "#F5F1E6";
};

const drawWood = (ctx, s) => {
  const grd = ctx.createLinearGradient(0, 0, s, s);
  grd.addColorStop(0, "#7a5230"); grd.addColorStop(0.5, "#5f3f22"); grd.addColorStop(1, "#4a2f18");
  ctx.fillStyle = grd; ctx.fillRect(0, 0, s, s);
  for (let i = 0; i < 46; i++) {
    ctx.strokeStyle = `rgba(${40 + Math.random() * 50},${26 + Math.random() * 30},${12 + Math.random() * 18},0.45)`;
    ctx.lineWidth = 1 + Math.random() * 3;
    const y = Math.random() * s;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(s * 0.33, y + (Math.random() - 0.5) * 26, s * 0.66, y + (Math.random() - 0.5) * 26, s, y + (Math.random() - 0.5) * 14);
    ctx.stroke();
  }
};

const drawPlate = (ctx, s) => {
  const GOLD = "#B2A475";
  ctx.fillStyle = "#0B0B0C"; ctx.fillRect(0, 0, s, s);
  // recessed border
  ctx.strokeStyle = "rgba(178,164,117,0.4)"; ctx.lineWidth = 6;
  ctx.strokeRect(24, 24, s - 48, s - 48);

  // QR (center / slightly left) — stylised
  const qr = Math.round(s * 0.4);
  const qx = Math.round(s / 2 - qr / 2), qy = Math.round(s / 2 - qr / 2);
  ctx.fillStyle = "#FFFFFF"; ctx.fillRect(qx - 10, qy - 10, qr + 20, qr + 20);
  const mods = 9, cell = qr / mods;
  ctx.fillStyle = "#000000";
  for (let i = 0; i < mods; i++) for (let j = 0; j < mods; j++) {
    if ((i * 7 + j * 3 + i * j) % 2 === 0) ctx.fillRect(qx + i * cell, qy + j * cell, cell - 1, cell - 1);
  }
  // finder squares
  const finder = (fx, fy) => {
    ctx.fillStyle = "#000"; ctx.fillRect(fx, fy, cell * 3, cell * 3);
    ctx.fillStyle = "#fff"; ctx.fillRect(fx + cell * 0.6, fy + cell * 0.6, cell * 1.8, cell * 1.8);
    ctx.fillStyle = "#000"; ctx.fillRect(fx + cell, fy + cell, cell, cell);
  };
  finder(qx, qy); finder(qx + cell * 6, qy); finder(qx, qy + cell * 6);

  // "MENU" vertical along left edge
  ctx.save();
  ctx.translate(s * 0.085, s / 2);
  ctx.rotate(Math.PI / 2);
  ctx.fillStyle = GOLD;
  ctx.font = "600 46px Georgia, serif";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.letterSpacing = "10px";
  ctx.fillText("MENU", 0, 0);
  ctx.restore();

  // NFC waves (top-right)
  ctx.strokeStyle = GOLD; ctx.lineWidth = 7; ctx.lineCap = "round";
  const nx = s * 0.78, ny = s * 0.22;
  for (let k = 1; k <= 3; k++) { ctx.beginPath(); ctx.arc(nx, ny, 14 * k, -Math.PI * 0.35, Math.PI * 0.35); ctx.stroke(); }
  ctx.fillStyle = GOLD; ctx.beginPath(); ctx.arc(nx - 6, ny, 6, 0, Math.PI * 2); ctx.fill();

  // small isometric cube logo (bottom-right)
  ctx.strokeStyle = GOLD; ctx.lineWidth = 4; ctx.lineJoin = "round";
  const cx = s * 0.8, cy = s * 0.8, r = 30;
  const pts = {
    t: [cx, cy - r], tr: [cx + r * 0.86, cy - r * 0.5], br: [cx + r * 0.86, cy + r * 0.5],
    b: [cx, cy + r], bl: [cx - r * 0.86, cy + r * 0.5], tl: [cx - r * 0.86, cy - r * 0.5], c: [cx, cy],
  };
  const line = (a, b) => { ctx.beginPath(); ctx.moveTo(...a); ctx.lineTo(...b); ctx.stroke(); };
  line(pts.t, pts.tr); line(pts.tr, pts.br); line(pts.br, pts.b); line(pts.b, pts.bl); line(pts.bl, pts.tl); line(pts.tl, pts.t);
  line(pts.t, pts.c); line(pts.br, pts.c); line(pts.bl, pts.c);
};

const CubeCanvas = ({ color, finish, material, sideText, sideLogo, className = "" }) => {
  const mountRef = useRef(null);
  const ctrlRef = useRef(null);
  const propsRef = useRef({});
  propsRef.current = { color, finish, material, sideText, sideLogo };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const width = mount.clientWidth, height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
    camera.position.set(0, 0.4, 6.1);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.touchAction = "none";
    renderer.domElement.style.cursor = "grab";

    // lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    scene.add(new THREE.HemisphereLight(0xfff4d6, 0x0b0b0c, 0.5));
    const key = new THREE.DirectionalLight(0xffffff, 1.15); key.position.set(3, 5, 4); scene.add(key);
    const rim = new THREE.DirectionalLight(0xB2A475, 0.7); rim.position.set(-4, 2, -3); scene.add(rim);
    const spec = new THREE.PointLight(0xffffff, 40, 40); spec.position.set(-2, 3, 4); scene.add(spec);

    // textures
    const sideCanvas = document.createElement("canvas"); sideCanvas.width = sideCanvas.height = 512;
    const sideCtx = sideCanvas.getContext("2d");
    const sideTex = new THREE.CanvasTexture(sideCanvas); sideTex.colorSpace = THREE.SRGBColorSpace; sideTex.anisotropy = 4;

    const plateCanvas = document.createElement("canvas"); plateCanvas.width = plateCanvas.height = 512;
    drawPlate(plateCanvas.getContext("2d"), 512);
    const plateTex = new THREE.CanvasTexture(plateCanvas); plateTex.colorSpace = THREE.SRGBColorSpace; plateTex.anisotropy = 4;

    const sideMat = new THREE.MeshPhysicalMaterial({ map: sideTex, color: 0xffffff, roughness: 0.1, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.06 });
    const plateMat = new THREE.MeshStandardMaterial({ map: plateTex, roughness: 0.9, metalness: 0.05 });
    const baseMat = new THREE.MeshStandardMaterial({ color: 0x0b0b0c, roughness: 0.95, metalness: 0 });
    // order: px, nx, py(top), ny(bottom), pz, nz
    const materials = [sideMat, sideMat, plateMat, baseMat, sideMat, sideMat];

    const geo = new THREE.BoxGeometry(2, 2, 2);
    const cube = new THREE.Mesh(geo, materials);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: 0xB2A475, transparent: true, opacity: 0.5 }));
    const group = new THREE.Group();
    group.add(cube);
    group.add(edges);
    group.rotation.set(-0.32, 0.6, 0);
    group.scale.setScalar(0.82);
    scene.add(group);

    const ctrl = {
      logoImg: null,
      redraw() {
        const p = propsRef.current;
        const s = 512;
        sideCtx.clearRect(0, 0, s, s);
        if (p.material === "legno") drawWood(sideCtx, s);
        else { sideCtx.fillStyle = p.color; sideCtx.fillRect(0, 0, s, s); }
        // subtle inner sheen
        const gr = sideCtx.createLinearGradient(0, 0, 0, s);
        gr.addColorStop(0, "rgba(255,255,255,0.10)"); gr.addColorStop(0.4, "rgba(255,255,255,0)");
        sideCtx.fillStyle = gr; sideCtx.fillRect(0, 0, s, s);
        if (this.logoImg) {
          const box = s * 0.6, iw = this.logoImg.width, ih = this.logoImg.height;
          const sc = Math.min(box / iw, box / ih), w = iw * sc, h = ih * sc;
          sideCtx.drawImage(this.logoImg, (s - w) / 2, (s - h) / 2, w, h);
        } else if (p.sideText) {
          sideCtx.fillStyle = readable(p.material === "legno" ? "#5f3f22" : p.color);
          sideCtx.textAlign = "center"; sideCtx.textBaseline = "middle";
          let fs = 150; sideCtx.font = `600 ${fs}px Georgia, serif`;
          while (sideCtx.measureText(p.sideText).width > s * 0.82 && fs > 28) { fs -= 6; sideCtx.font = `600 ${fs}px Georgia, serif`; }
          sideCtx.fillText(p.sideText, s / 2, s / 2);
        }
        sideTex.needsUpdate = true;
      },
      applyMaterial() {
        const p = propsRef.current;
        if (p.material === "legno") { sideMat.roughness = 0.55; sideMat.clearcoat = 0.18; sideMat.clearcoatRoughness = 0.4; }
        else if (p.finish === "lucido") { sideMat.roughness = 0.06; sideMat.clearcoat = 1; sideMat.clearcoatRoughness = 0.05; }
        else { sideMat.roughness = 0.82; sideMat.clearcoat = 0; }
        sideMat.needsUpdate = true;
      },
    };
    ctrl.redraw(); ctrl.applyMaterial();
    ctrlRef.current = ctrl;

    // ---- drag rotation ----
    let dragging = false, lastX = 0, lastY = 0, autoIdle = 0;
    const onDown = (e) => { dragging = true; autoIdle = 0; lastX = e.clientX; lastY = e.clientY; renderer.domElement.style.cursor = "grabbing"; try { renderer.domElement.setPointerCapture(e.pointerId); } catch (_) {} };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY; lastX = e.clientX; lastY = e.clientY;
      group.rotation.y += dx * 0.01;
      group.rotation.x = Math.max(-1.2, Math.min(1.2, group.rotation.x + dy * 0.01));
    };
    const onUp = (e) => { dragging = false; autoIdle = 0; renderer.domElement.style.cursor = "grab"; try { renderer.domElement.releasePointerCapture(e.pointerId); } catch (_) {} };
    renderer.domElement.addEventListener("pointerdown", onDown);
    renderer.domElement.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);

    let raf, last = performance.now();
    const tick = () => {
      const now = performance.now(); const dt = Math.min((now - last) / 1000, 0.033); last = now;
      if (!dragging) { autoIdle += dt; if (autoIdle > 1.2) group.rotation.y += 0.004; }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
    };
    const ro = new ResizeObserver(onResize); ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf); ro.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onDown);
      renderer.domElement.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      geo.dispose(); sideMat.dispose(); plateMat.dispose(); baseMat.dispose();
      edges.geometry.dispose(); edges.material.dispose();
      sideTex.dispose(); plateTex.dispose(); renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      ctrlRef.current = null;
    };
  }, []);

  // react to control changes
  useEffect(() => {
    const c = ctrlRef.current; if (!c) return;
    if (sideLogo) {
      const img = new Image();
      img.onload = () => { c.logoImg = img; c.redraw(); };
      img.src = sideLogo;
    } else { c.logoImg = null; c.redraw(); }
    c.applyMaterial();
  }, [color, finish, material, sideText, sideLogo]);

  return <div ref={mountRef} className={className} data-testid="cube-canvas" />;
};

export default CubeCanvas;
